import {
  Component, OnInit, ViewChild, AfterViewChecked, computed, inject, signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import {
  LucideAngularModule,
  LayoutDashboard,
  Folder,
  Layers,
  Boxes,
  CircleQuestionMark,
  ScrollText,
  Receipt,
  Settings,
  Inbox,
  Bell,
  Users,
  Building2,
  MessageSquarePlus,
  MessagesSquare,
  MessageSquareDot,
  SlidersHorizontal,
  ShieldCheck,
  TicketPercent,
} from 'lucide-angular';
import { SettingsService } from '../../../core/services/settings.service';
import { ActiveTenantService } from '../../../core/services/active-tenant.service';
import { AuthService } from '../../../core/services/auth.service';
import { VersionService } from '../../../core/services/version.service';
import { PlatformAdminService } from '../../../core/services/platform-admin.service';
import { PermissionsService } from '../../../core/services/permissions.service';
import { AnnouncementsService } from '../../../core/services/announcements.service';
import { DOCS_URL } from '../../../core/utils/external-links';
import { FeedbackModalComponent } from '../../../features/feedback/feedback-modal/feedback-modal.component';
import { FeedbackService } from '../../../core/services/feedback.service';
import { PlatformSettingsService } from '../../../core/services/platform-settings.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, LucideAngularModule, FeedbackModalComponent],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, AfterViewChecked {
  readonly settingsService = inject(SettingsService);
  readonly authService = inject(AuthService);
  readonly versionService = inject(VersionService);
  protected readonly activeTenantService = inject(ActiveTenantService);
  protected platformAdminService = inject(PlatformAdminService);
  private readonly permissionsService = inject(PermissionsService);
  protected readonly announcementsService = inject(AnnouncementsService);

  // Visibility signals
  protected readonly canSeeAuditLogs = this.permissionsService.hasTenantCapability('audit_log_viewer');
  protected readonly canSeeSecurity  = this.permissionsService.hasTenantCapability('security_viewer');
  protected readonly canSeeBilling   = this.permissionsService.hasTenantCapability('billing_access');
  protected readonly canSeeMembers   = this.permissionsService.hasTenantRole('owner');
  protected readonly canSeeSettings  = this.permissionsService.hasTenantRole('owner');
  protected readonly tenantCount     = computed(() => this.activeTenantService.tenants().length);
  /** A soft-deleted tenant 410s every tenant-scoped route (docs/adr/0008) - only the tenant list, Settings (which holds Restore) and the me-scoped pages still work. */
  protected readonly activeTenantDeleted = computed(() => !!this.activeTenantService.active()?.deleted_at);

  // Icon references
  protected readonly DashboardIcon      = LayoutDashboard;
  protected readonly FolderIcon         = Folder;
  protected readonly BillingIcon        = Receipt;
  protected readonly PlansIcon          = Layers;
  protected readonly AuditLogsIcon      = ScrollText;
  protected readonly SecurityIcon       = ShieldCheck;
  protected readonly SettingsIcon       = Settings;
  protected readonly InboxIcon          = Inbox;
  protected readonly BellIcon           = Bell;
  protected readonly HelpIcon           = CircleQuestionMark;
  protected readonly MembersIcon        = Users;
  protected readonly MegaphoneIcon      = Bell;
  protected readonly TenantsIcon        = Building2;
  protected readonly FeedbackIcon          = MessageSquarePlus;
  protected readonly MyFeedbackIcon        = MessagesSquare;
  protected readonly FeedbackAdminIcon     = MessageSquareDot;
  protected readonly PlatformSettingsIcon  = SlidersHorizontal;
  protected readonly EnvironmentsIcon      = Boxes;
  protected readonly PromoCodesIcon        = TicketPercent;
  protected readonly docsUrl = DOCS_URL;

  protected readonly feedbackService = inject(FeedbackService);
  protected readonly platformSettingsService = inject(PlatformSettingsService);

  // Feedback modal state
  readonly feedbackOpen = signal(false);

  @ViewChild('feedbackModal') feedbackModalRef?: FeedbackModalComponent;

  openFeedback(): void {
    this.feedbackService.loadConfig();
    this.feedbackOpen.set(true);
  }

  closeFeedback(): void {
    this.feedbackOpen.set(false);
  }

  ngAfterViewChecked(): void {
    if (this.feedbackModalRef && !this.feedbackModalRef.onClose) {
      this.feedbackModalRef.onClose = () => this.closeFeedback();
    }
  }

  ngOnInit(): void {
    if (!this.activeTenantService.loaded()) this.activeTenantService.loadTenants();
    if (this.authService.isPlatformAdmin()) {
      this.platformAdminService.loadRequests().subscribe({ error: () => {} });
    }
    this.announcementsService.loadUnreadCount();
    if (this.authService.isPlatformAdmin()) {
      this.platformSettingsService.loadAdminUnreadCount();
      this.platformSettingsService.loadRestartStatus();
    } else {
      this.feedbackService.loadUserUnreadCount();
    }
  }

  readonly tenantSettingsLink = computed(() => {
    const tid = this.activeTenantService.activeId();
    return tid ? ['/tenants', tid, 'settings'] : null;
  });

  readonly tenantMembersLink = computed(() => {
    const tid = this.activeTenantService.activeId();
    return tid ? ['/tenants', tid, 'members'] : null;
  });
}
